import { isAxiosError } from "axios";
import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import type {
  AiChatLayoutPage,
  AiChatChangesPage,
  AiChatQuestionAnswer,
  AiChatQuestionResult,
  AiChatGuardianDecision,
  AiChatGuardianReview,
  AiChatGuardianReviewPage,
  AiChatItemBodyChunk,
  AiChatItemBodyInput,
  AiChatListThreadsInput,
  AiChatReadReceipt,
  AiChatRun,
  AiChatRunStart,
  AiChatSearchInput,
  AiChatSearchPage,
  AiChatStartRunInput,
  AiChatSteeringMessage,
  AiChatSteerRunInput,
  AiChatStreamConnection,
  AiChatStreamEvent,
  AiChatStreamInput,
  AiChatStreamToken,
  AiChatThreadDetail,
  AiChatThreadHeader,
  AiChatThreadPage,
  AiChatTimelineInput,
  AiChatTimelineItem,
  AiChatTimelinePage,
  ChatApi,
} from "@/lib/types/ai-chat";

const AI_CHAT_ROUTE = "intelligence/v1";

type ApiEnvelope<T> = {
  data: T;
  meta: { userMsg: string };
  pagination?: Record<string, unknown>;
};

type ThreadsData = { conversations: AiChatThreadHeader[] };
type ThreadData = {
  readOnly?: boolean;
  conversation: AiChatThreadHeader;
  revision: number;
  activeRunId: string | null;
};
type TimelineData = {
  conversationId: string;
  messages: AiChatTimelineItem[];
  revision: number;
};
type SearchData = {
  conversationId: string;
  items: AiChatSearchPage["items"];
  revision: number;
};
type ChangesData = {
  conversationId: string;
  items: AiChatTimelineItem[];
  conversation: AiChatThreadHeader;
  resyncRequired: boolean;
};

const paginationBoolean = (
  pagination: Record<string, unknown> | undefined,
  key: string,
) => pagination?.[key] === true;

const paginationString = (
  pagination: Record<string, unknown> | undefined,
  key: string,
) => {
  const value = pagination?.[key];
  return typeof value === "string" ? value : null;
};

export const getAiChatLayout = async (
  threadId: string,
  input: { cursor?: string; limit?: number; signal?: AbortSignal } = {},
): Promise<AiChatLayoutPage> => {
  const { signal, ...params } = input;
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<{
      conversationId: string;
      items: AiChatLayoutPage["items"];
      revision: number;
    }>
  >(`${AI_CHAT_ROUTE}/conversations/${encodeURIComponent(threadId)}/layout`, {
    params,
    signal,
  });
  return {
    threadId: response.data.data.conversationId,
    items: response.data.data.items,
    revision: response.data.data.revision,
    nextCursor: paginationString(response.data.pagination, "nextCursor"),
    hasMore: paginationBoolean(response.data.pagination, "hasMore"),
  };
};

export const listAiChatThreads = async (
  input: AiChatListThreadsInput = {},
): Promise<AiChatThreadPage> => {
  const { signal, ...params } = input;
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<ThreadsData>
  >(`${AI_CHAT_ROUTE}/conversations`, { params, signal });
  return {
    threads: response.data.data.conversations,
    nextCursor: paginationString(response.data.pagination, "nextCursor"),
    hasMore: paginationBoolean(response.data.pagination, "hasMore"),
  };
};

export const getAiChatThread = async (
  threadId: string,
  signal?: AbortSignal,
): Promise<AiChatThreadDetail> => {
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<ThreadData>
  >(`${AI_CHAT_ROUTE}/conversations/${encodeURIComponent(threadId)}`, {
    signal,
  });
  const { conversation, ...detail } = response.data.data;
  return { thread: conversation, ...detail };
};

export const getAiChatTimeline = async (
  threadId: string,
  input: AiChatTimelineInput = {},
): Promise<AiChatTimelinePage> => {
  if (
    input.aroundItemId !== undefined &&
    (input.before !== undefined || input.after !== undefined)
  ) {
    throw new Error("aroundItemId cannot be combined with before or after.");
  }
  const { signal, ...params } = input;
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<TimelineData>
  >(`${AI_CHAT_ROUTE}/conversations/${encodeURIComponent(threadId)}/messages`, {
    params: { ...params, compact: true },
    signal,
  });
  const { data, pagination } = response.data;
  return {
    threadId: data.conversationId,
    items: data.messages,
    revision: data.revision,
    beforeCursor: paginationString(pagination, "beforeCursor"),
    afterCursor: paginationString(pagination, "afterCursor"),
    hasMoreBefore: paginationBoolean(pagination, "hasMoreBefore"),
    hasMoreAfter: paginationBoolean(pagination, "hasMoreAfter"),
  };
};

export const getAiChatItemBody = async (
  threadId: string,
  itemId: string,
  input: AiChatItemBodyInput,
): Promise<AiChatItemBodyChunk> => {
  const { signal, ...params } = input;
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<AiChatItemBodyChunk>
  >(
    `${AI_CHAT_ROUTE}/conversations/${encodeURIComponent(threadId)}/items/${encodeURIComponent(itemId)}`,
    { params, signal },
  );
  return response.data.data;
};

export const searchAiChatThread = async (
  threadId: string,
  input: AiChatSearchInput,
): Promise<AiChatSearchPage> => {
  const { signal, query: q, ...params } = input;
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<SearchData>
  >(`${AI_CHAT_ROUTE}/conversations/${encodeURIComponent(threadId)}/search`, {
    params: { ...params, q, compact: true },
    signal,
  });
  return {
    threadId: response.data.data.conversationId,
    items: response.data.data.items,
    revision: response.data.data.revision,
    nextCursor: paginationString(response.data.pagination, "nextCursor"),
    hasMore: paginationBoolean(response.data.pagination, "hasMore"),
  };
};

export const markAiChatThreadRead = async (
  threadId: string,
  revision: number,
): Promise<AiChatReadReceipt> => {
  const response = await authenticatedFastApiAxiosInstance.post<
    ApiEnvelope<{
      conversation: AiChatThreadHeader;
      revision: number;
      lastSeenRevision: number;
    }>
  >(`${AI_CHAT_ROUTE}/conversations/${encodeURIComponent(threadId)}/read`, {
    revision,
  });
  const { conversation, ...receipt } = response.data.data;
  return { thread: conversation, ...receipt };
};

export const getAiChatChanges = async (
  threadId: string,
  input: { cursor?: string; limit?: number; signal?: AbortSignal } = {},
): Promise<AiChatChangesPage> => {
  const { signal, ...params } = input;
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<ChangesData>
  >(`${AI_CHAT_ROUTE}/conversations/${encodeURIComponent(threadId)}/changes`, {
    params: { ...params, compact: true },
    signal,
  });
  const nextCursor = paginationString(response.data.pagination, "nextCursor");
  if (nextCursor === null) {
    throw new Error("The chat changes response did not include a cursor.");
  }
  return {
    threadId: response.data.data.conversationId,
    items: response.data.data.items,
    thread: response.data.data.conversation,
    resyncRequired: response.data.data.resyncRequired,
    nextCursor,
    hasMore: paginationBoolean(response.data.pagination, "hasMore"),
  };
};

export const listAiChatGuardianReviews = async (
  runId: string,
  input: Parameters<ChatApi["listGuardianReviews"]>[1] = {},
): Promise<AiChatGuardianReviewPage> => {
  const { signal, ...params } = input;
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<{ runId: string; reviews: AiChatGuardianReview[] }>
  >(`${AI_CHAT_ROUTE}/runs/${encodeURIComponent(runId)}/guardian-reviews`, {
    params,
    signal,
  });
  return {
    ...response.data.data,
    nextCursor: paginationString(response.data.pagination, "nextCursor"),
    hasMore: paginationBoolean(response.data.pagination, "hasMore"),
  };
};

export const getAiChatGuardianReview = async (
  runId: string,
  reviewId: string,
  signal?: AbortSignal,
): Promise<AiChatGuardianReview> => {
  const response = await authenticatedFastApiAxiosInstance.get<
    ApiEnvelope<{ review: AiChatGuardianReview }>
  >(
    `${AI_CHAT_ROUTE}/runs/${encodeURIComponent(runId)}/guardian-reviews/${encodeURIComponent(reviewId)}`,
    { signal },
  );
  return response.data.data.review;
};

export const decideAiChatGuardianReview = async (
  runId: string,
  reviewId: string,
  decision: AiChatGuardianDecision,
): Promise<AiChatGuardianReview> => {
  const response = await authenticatedFastApiAxiosInstance.post<
    ApiEnvelope<{ review: AiChatGuardianReview }>
  >(
    `${AI_CHAT_ROUTE}/runs/${encodeURIComponent(runId)}/guardian-reviews/${encodeURIComponent(reviewId)}/decision`,
    { decision },
  );
  return response.data.data.review;
};

export const answerAiChatQuestion = async (
  runId: string,
  requestId: string,
  answers: AiChatQuestionAnswer[],
): Promise<AiChatQuestionResult> => {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await authenticatedFastApiAxiosInstance.post<
        ApiEnvelope<AiChatQuestionResult>
      >(
        `${AI_CHAT_ROUTE}/runs/${encodeURIComponent(runId)}/questions/${encodeURIComponent(requestId)}/answer`,
        { answers },
      );
      return response.data.data;
    } catch (error) {
      const code = isAxiosError(error)
        ? (error.response?.data?.error?.code ??
          error.response?.data?.detail?.code)
        : undefined;
      if (
        !isAxiosError(error) ||
        error.response?.status !== 409 ||
        code !== "run_not_accepting" ||
        attempt >= 3
      )
        throw error;
      await new Promise((resolve) => setTimeout(resolve, 250 * 2 ** attempt));
    }
  }
};

export const startAiChatRun = async (
  input: AiChatStartRunInput,
): Promise<AiChatRunStart> => {
  const response = await authenticatedFastApiAxiosInstance.post<
    ApiEnvelope<AiChatRunStart>
  >(`${AI_CHAT_ROUTE}/runs/start`, input);
  return response.data.data;
};

export const steerAiChatRun = async (
  runId: string,
  input: AiChatSteerRunInput,
): Promise<AiChatSteeringMessage> => {
  const response = await authenticatedFastApiAxiosInstance.post<
    ApiEnvelope<{ steering: AiChatSteeringMessage }>
  >(`${AI_CHAT_ROUTE}/runs/${encodeURIComponent(runId)}/steer`, input);
  return response.data.data.steering;
};

export const cancelAiChatRun = async (runId: string): Promise<AiChatRun> => {
  const response = await authenticatedFastApiAxiosInstance.post<
    ApiEnvelope<{ run: AiChatRun }>
  >(`${AI_CHAT_ROUTE}/runs/${encodeURIComponent(runId)}/cancel`);
  return response.data.data.run;
};

export const getAiChatStreamToken = async (
  runId: string,
): Promise<AiChatStreamToken> => {
  const response = await authenticatedFastApiAxiosInstance.post<
    ApiEnvelope<AiChatStreamToken>
  >(`${AI_CHAT_ROUTE}/runs/${encodeURIComponent(runId)}/stream-attach-token`);
  return response.data.data;
};

const parseStreamEvent = (value: unknown): AiChatStreamEvent => {
  if (!value || typeof value !== "object" || !("type" in value)) {
    throw new Error("The chat stream returned an invalid message.");
  }
  const type = (value as { type?: unknown }).type;
  if (
    type !== "thread.snapshot" &&
    type !== "thread.changes" &&
    type !== "thread.closed" &&
    type !== "thread.error"
  ) {
    throw new Error("The chat stream returned an unsupported message.");
  }
  return value as AiChatStreamEvent;
};

export const openAiChatRunStream = async (
  input: AiChatStreamInput,
): Promise<AiChatStreamConnection> => {
  const token = await getAiChatStreamToken(input.runId);
  const url = new URL(token.websocketUrl);
  url.searchParams.set("compact", "true");
  if (input.cursor) url.searchParams.set("cursor", input.cursor);
  if (input.limit) url.searchParams.set("limit", String(input.limit));
  const socket = new WebSocket(url, token.subprotocols);
  socket.onopen = () => input.onOpen?.();
  socket.onmessage = (event) => {
    try {
      input.onEvent(parseStreamEvent(JSON.parse(String(event.data))));
    } catch (error) {
      input.onError?.(
        error instanceof Error ? error : new Error("Chat stream failed."),
      );
      // Browsers allow 1000 or application codes; a non-normal close reconnects.
      socket.close(4003, "Invalid chat stream message");
    }
  };
  socket.onerror = () => input.onError?.(new Error("Chat stream failed."));
  socket.onclose = (event) =>
    input.onClose?.({ code: event.code, reason: event.reason });
  return {
    close: () => {
      socket.close(1000, "Chat stream closed");
    },
  };
};

export const chatApi: ChatApi = {
  getLayout: getAiChatLayout,
  listThreads: listAiChatThreads,
  getThread: getAiChatThread,
  getTimeline: getAiChatTimeline,
  getItemBody: getAiChatItemBody,
  searchThread: searchAiChatThread,
  markThreadRead: markAiChatThreadRead,
  getChanges: getAiChatChanges,
  listGuardianReviews: listAiChatGuardianReviews,
  getGuardianReview: getAiChatGuardianReview,
  decideGuardianReview: decideAiChatGuardianReview,
  answerQuestion: answerAiChatQuestion,
  startRun: startAiChatRun,
  steerRun: steerAiChatRun,
  cancelRun: cancelAiChatRun,
  getStreamToken: getAiChatStreamToken,
  openRunStream: openAiChatRunStream,
};
